import api from './api';

/**
 * Release Service - Handles all release/distribution related API calls
 */
const releaseService = {
  /**
   * Get all distributions (releases) with optional filtering parameters
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} Resolves to list of distributions
   */
  getAll: async (params) => {
    const response = await api.get('api/distributions', { params });
    return response.data;
  },

  /**
   * Get a specific distribution by ID
   * @param {string} id - Distribution ID
   * @returns {Promise} Resolves to distribution details
   */
  get: async (id) => {
    const response = await api.get(`api/distributions/${id}`);
    return response.data;
  },

  /**
   * Delete a distribution
   * @param {string} id - Distribution ID
   * @returns {Promise} Resolves to deletion result
   */
  remove: async (id) => {
    const response = await api.delete(`api/distributions/${id}`);
    return response.data;
  },

  /**
   * Save a distribution (create or update)
   * @param {Object} distribution - Distribution data
   * @returns {Promise} Resolves to saved distribution
   */
  save: async (distribution) => {
    const data = JSON.parse(JSON.stringify(distribution));

    if (data._id) {
      // Update existing distribution
      const id = data._id;

      // Remove fields that should not be included in update
      delete data._id;
      delete data.createdAt;
      delete data.updatedAt;
      delete data.log;
      delete data.logString;

      // Handle artist reference
      if (data.artist && data.artist._id) {
        data.artist = data.artist._id;
      }

      const response = await api.put(`api/distributions/${id}`, data);
      return response.data;
    } else {
      // Create new distribution
      const response = await api.post('api/distributions', distribution);
      return response.data;
    }
  },

  /**
   * Get store links for a distribution
   * @param {string} id - Distribution ID
   * @returns {Promise} Resolves to store links
   */
  getStores: async (id) => {
    const response = await api.get(`api/distribution/${id}/stores`);
    return response.data;
  },

  /**
   * Get copyright issues for a specific song in a distribution
   * @param {string} id - Distribution ID
   * @param {string} songId - Song ID
   * @returns {Promise} Resolves to copyright issues
   */
  getCopyrightIssue: async (id, songId) => {
    const response = await api.get('api/copyright-issues', {
      params: {
        distribution: id,
        song_id: songId,
      },
    });

    return response.data;
  },

  /**
   * Check how many releases are available to create in the current month
   * @returns {Promise} Resolves to distribution limit info
   */
  getAvailableDistributions: async () => {
    const response = await api.get('api/distribution-limit');
    return response.data;
  },

  /**
   * Get information about a release without being authenticated
   * @param {string} key - Public release key
   * @returns {Promise} Resolves to public release data
   */
  getPublicRelease: async (key) => {
    const response = await api.get(`api/release/${key}`);
    return response.data;
  },

  /**
   * Get available DSPs for a release
   * @returns {Promise} Resolves to available DSPs
   */
  getAvailableDsps: async () => {
    const response = await api.get('api/distribution/dsps');
    return response.data;
  },

  /**
   * Request a takedown for a release
   * @param {string} id - Release ID
   * @returns {Promise} Resolves to takedown request result
   */
  requestTakedown: async (id, extraData) => {
    const response = await api.put(`api/takedown/${id}`, extraData || {});
    return response.data;
  },

  /**
   * Cancel a takedown request for a release
   * @param {string} id - Release ID
   * @returns {Promise} Resolves to cancel result
   */
  cancelTakedown: async (id) => {
    const response = await api.delete(`api/takedown/${id}`, {
      status: 3, // Published status
      takedown_reason: '',
      takedown_request_on: '',
    });
    return response.data;
  },

  /**
   * Get what's next content for a distribution (pre-release or after-release tips)
   * @param {string} id - Distribution ID
   * @returns {Promise} Resolves to whats-next data (preRelease / afterRelease categories)
   */
  getWhatsNext: async (id) => {
    const response = await api.get('api/distribution-whats-next', {
      params: { distributionId: id },
    });
    return response.data;
  },

  /**
   * Submit a metadata change request for a distribution
   * @param {string} id - Distribution ID
   * @param {Object} payload - Metadata change request payload
   * @returns {Promise} Resolves to the created metadata request
   */
  submitMetadataRequest: async (id, payload) => {
    const response = await api.post(`api/distribution/${id}/metadata-request`, payload);
    return response.data;
  },

  /**
   * Get all metadata change requests for the current user
   * @returns {Promise} Resolves to list of metadata requests
   */
  getMetadataRequests: async () => {
    const response = await api.get('api/distribution/metadata-requests');
    return response.data;
  },
};

export default releaseService;